import React, { useState, useEffect } from "react";
import ApiService from "../../services/Api.service";
import moment from "moment";
import "./CallDetails.css";
import VoiceAnalyticsAiModel from "../VoiceAnalyticsPopUpModel/VoiceAnalyticsAiModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../Alert/Alert";
import Pagination from "../Pagination/Pagination";
import { Eye, EyeOffIcon, RotateCw } from "lucide-react";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../FilterBar/FilterBar";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

function CallDetails({ viewType }) {
  const [Loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [sections, setSections] = useState([]);
  const [processName, setProcessName] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [headers, setHeaders] = useState([]);
  const [endSection, setEndSection] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [dropDownData, setDropDownData] = useState([]);
  const [dispositionDropDownData, setDispositionDropDownData] = useState([]);
  const [auditStatusDropDown, setAuditStatusDropDown] = useState([]);
  const [agentsDropdown, setAgentsDropdown] = useState([]);

  const currentDate = new Date().toISOString().split("T")[0];
  const [alertValue, setAlertValue] = useState({
    show: false,
    message: "",
    variant: "success",
    position: "middle",
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [filterBarData, setFilterBarData] = useState(null);

  const validationSchema = yup.object().shape({
  });

  const {
    handleSubmit,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      companyCode: "",
      start_date: currentDate,
      end_date: currentDate,
      status: "All",
      disposition: "All",
      agent: "",
      operator: "=",
      accuracy_score: 0,
    },
  });

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    fetchDetail(1, postsPerPage, filterBarData)
  };

  const [syncingCallId, setSyncingCallId] = useState(null);

  const handleRowClick = (row, index) => {
    setSelectedRow(row);
    setSelectedRowIndex(index);
    setShowModal(true);
  };

  const handleSyncToCqa = async (item, e) => {
    if (e) e.stopPropagation();
    const callId = item.id || item._id;
    if (!callId) return;

    try {
      setSyncingCallId(callId);
      const res = await ApiService.syncCallToCqa(callId);
      if (res && res.status === 200) {
        setAlertValue({
          show: true,
          message: res.data?.message || "Call successfully synced to CQA! Audit In-Process.",
          variant: "success",
        });
        setTableData((prev) =>
          prev.map((c) =>
            (c.id === callId || c._id === callId)
              ? { ...c, audit_status: res.data?.cqa_status || "In-Process", audit_call_id: res.data?.audit_call_id }
              : c
          )
        );
      } else {
        setAlertValue({
          show: true,
          message: res.data?.detail || "Failed to sync call to CQA",
          variant: "danger",
        });
      }
    } catch (err) {
      console.error("handleSyncToCqa error:", err);
      setAlertValue({
        show: true,
        message: err.response?.data?.detail || "Failed to sync call to CQA",
        variant: "danger",
      });
    } finally {
      setSyncingCallId(null);
      setTimeout(() => {
        setAlertValue({ show: false, message: "", variant: "" });
      }, 3000);
    }
  };

  const fetchDetail = async (page = 1, size = postsPerPage, payload = filterBarData) => {
    try {
      setLoading(true);
      if (!payload) {
        payload = {
          subprocess_id: "",
          start_date: "",
          end_date: "",
          status: "All",
          disposition: "All",
          agent: "",
          operator: "=",
          accuracy_score: 0,
        };
      }
      let res;
      if (viewType === "report") {
        res = await ApiService.getAuditReport(payload, { page, size });
      } else {
        res = await ApiService.getTranscriptionDetails(payload, { page, size });
      }
      if (res.status === 200 && res.data) {
        setTableData(res.data.data);
        setPagination(res.data.pagination);
        setHeaders(res.data.header);
        setSections(res.data.sections);
        setEndSection(res?.data?.end_section);
        setProcessName(res.data?.data[0]?.process_name || "");
        setSelectedRowIndex(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownDetail = async () => {
    try {
      const subprocessRes = await ApiService.getSubprocesses();
      if (subprocessRes.status === 200) {
        const list = Array.isArray(subprocessRes.data)
          ? subprocessRes.data
          : (subprocessRes.data?.subprocesses || subprocessRes.data?.data || []);
        setDropDownData(list);
      }

      const auditStatusRes = await ApiService.getAuditStatus();
      if (auditStatusRes.status === 200) {
        const statusList = auditStatusRes.data?.audit_status || (Array.isArray(auditStatusRes.data) ? auditStatusRes.data : []);
        setAuditStatusDropDown(statusList);
      }

    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDropdownDetail();
    fetchDetail();
  }, []);

  const handelSubmit = async (data) => {
    const payload = {
      policy_number: data.policy_number,
      subprocess_id: data.companyCode,
      start_date: moment(data.start_date).format("YYYY-MM-DD"),
      end_date: moment(data.end_date).format("YYYY-MM-DD"),
      status: data.status,
      disposition: data.disposition,
      agent: data.agent,
      operator: data.operator,
      accuracy_score: Number(data.accuracy_score),
    };
    fetchDetail(1, postsPerPage, payload);
    setFilterBarData(payload)
  };

  const handleClearFilters = () => {
    reset({
      policy_number: "",
      companyCode: "",
      start_date: currentDate,
      end_date: currentDate,
      status: "All",
      disposition: "All",
      agent: "",
      operator: "=",
      accuracy_score:0,
    });
    fetchDetail();
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(1, newSize);
    setSelectedRowIndex(null);
  };


  const mistableTh = {
    backgroundColor: "#2B3A67",
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: "16px",
    letterSpacing: "0.1em",
    textAlign: "center",
  };

  const tableTdStyles = {
    backgroundColor: "#F7F7F7",
    fontSize: "12px",
    fontWeight: "700",
    lineHeight: "16px",
    letterSpacing: "0em",
    textAlign: "center",
    color: "#232122",
  };

  const filterConfig = [
    {
      name: "policy_number",
      label: "Policy Number",
      type: "text",
      placeholder: "Enter Policy Number .."
    },
    {
      name: "companyCode",
      label: "Company",
      type: "select",
      placeholder: "Select Templete", 
      options: (Array.isArray(dropDownData) ? dropDownData : []).map((item) => ({ label: item.name, value: item.id })),
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: "Select Status",  
      options: (Array.isArray(auditStatusDropDown) ? auditStatusDropDown : []).map((item) => ({
        label: item.label,
        value: item.value,
      })),
      defaultValue: "All",
    },
    {
      name: "disposition",
      label: "Disposition",
      type: "select",
      placeholder: "Select Disposition",
      options: (Array.isArray(dispositionDropDownData) ? dispositionDropDownData : []).map((item) => ({
        label: item.label,
        value: item.value,
      })),
      defaultValue: "All",
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      defaultValue: currentDate,
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      defaultValue: currentDate,
    },
    {
      name: "agent",
      label: "Agents",
      type: "multiselect",
      placeholder: "Select Agent",
      options: (Array.isArray(agentsDropdown) ? agentsDropdown : []).map((item) => ({
        label: item.label,
        value: item.value,
      })),
      defaultValue: "",
    },
    {
      name: "operator",
      label: "Operator",
      type: "select",
      placeholder: "Select Operator",
      options: [
        { label: "Greater Than or Equal (>=)", value: ">=" },
        { label: "Greater Than (>)", value: ">" },
        { label: "Less Than (<)", value: "<" },
        { label: "Less Than or Equal (<=)", value: "<=" },
        { label: "Equal (=)", value: "=" },
      ],
      defaultValue: "",
    },
    {
      name: "accuracy_score",
      label: "Accuracy Score",
      type: "text",
      placeholder: "Accuracy Score .."
    },
  ];

  const auditFilterConfig = [
    {
      name: "policy_number",
      label: "Policy Number",
      type: "text",
      placeholder: "Enter Policy Number .."
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      defaultValue: currentDate,
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      defaultValue: currentDate,
    },
  ];

  return (
    <>
      {alertValue.show && (
        <Alert
          variant={alertValue.variant}
          message={alertValue.message}
          dismissible={true}
          timeout={2500}
          position={alertValue.position}
          onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
        />
      )}

      <div className="main-contanier dailymis-main">
        <FilterBar
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={handelSubmit}
          onClearFilters={handleClearFilters}
          filters={viewType === "report" ? auditFilterConfig : filterConfig}  // 👈 dynamic filters here
          title={viewType === "report" ? "Audit Report" : "Transcription Details"}
          totalCount={pagination.total || 0}
          tableId="table-id"
          isDownload={viewType === "report" ? true : false}
        />

        <div className="table-container" >
          {Loading ? (
            <SkeletonLoader
              type="table"
              rows={8}
              cols={viewType === "report" ? 10 : (headers?.length > 0 ? headers.length + 2 : 7)}
            />
          ) : (
            <table id="table-id" className="custom-table-container">
            <thead
              className="table-head"
              style={{ position: "sticky", top: "0", zIndex: "1" }}
            >
              {viewType === "report" && (
                <>
                  <tr>
                    <th style={mistableTh} colSpan={headers.length + 1}>
                      {processName}
                    </th>
                    {sections.map((record, recordIndex) => (
                      <th
                        key={recordIndex}
                        colSpan={record?.parameter?.length > 0 ? record.parameter.length : 1}
                        style={mistableTh}
                      >
                        {record.name}
                      </th>
                    ))}
                    {endSection.length > 0 ? (
                      <th style={mistableTh} colSpan={endSection.length + 1}>
                      </th>
                    ) : ("")}
                  </tr>
                  <tr>
                    <th style={mistableTh}>S.No</th>
                    {headers.map((header, i) => (
                      <th key={i} style={mistableTh}>
                        {header.label}
                      </th>
                    ))}
                    {sections.map((record, recordIndex) =>
                      record.parameter.length > 0 ? (
                        record.parameter.map((param, paramIndex) => (
                          <th key={`${recordIndex}-${paramIndex}`} style={mistableTh}>
                            {param.name}
                          </th>
                        ))
                      ) : (
                        <th key={`${recordIndex}-empty`} style={mistableTh}>
                          --
                        </th>
                      )
                    )}
                    {endSection.map((header, i) => (
                      <th key={i} style={mistableTh}>
                        {header.label}
                      </th>
                    ))}
                  </tr>
                </>
              )}

              {viewType !== "report" && (
                <tr>
                  <th style={mistableTh}>S.No</th>
                  {headers.map((header, i) => (
                    <th key={i} style={mistableTh}>
                      {header.label}
                    </th>
                  ))}
                  <th style={mistableTh}>Action</th>
                </tr>
              )}

            </thead>

            <tbody>
              {tableData.length > 0 ? (
                tableData.map((item, index) => (
                  <tr key={index} style={{ cursor: "pointer" }}>
                    <td
                      style={{
                        ...tableTdStyles,
                        backgroundColor:
                          selectedRowIndex === index ? "#f9faff" : "transparent",
                      }}
                    >
                      {(pagination.page - 1) * pagination.size + index + 1}
                    </td>
                    {headers.map((header, i) => (
                      <td
                        key={i}
                        style={{
                          ...tableTdStyles,
                          backgroundColor:
                            selectedRowIndex === index ? "#f9faff" : "transparent",
                        }}
                        title={item[header.value] ?? "--"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(item, index);
                        }}
                      >
                        {header.value === "status" ? (
                          <StatusBadge status={item.status} />
                        ) : header.value === "disposition" ? (
                          <StatusBadge status={item.disposition} />
                        ) : typeof item[header.value] === "string" && item[header.value]?.length > 25 ? (
                          <span className="cell-paragraph-ellipsis" title={item[header.value]}>
                            {item[header.value]}
                          </span>
                        ) : (
                          item[header.value] ?? "--"
                        )}
                      </td>
                    ))}
                    {viewType === "report" &&
                      item.sections?.map((record, recordIndex) =>
                        record.parameter?.length > 0 ? (
                          record.parameter.map((param, paramIndex) => (
                            <td
                              key={`${recordIndex}-${paramIndex}`}
                              style={{
                                ...tableTdStyles,
                                backgroundColor:
                                  selectedRowIndex === index ? "#f9faff" : "transparent",
                              }}
                              title={typeof param.answer === "string" ? param.answer : (param.answer ?? "--")}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(item, index);
                              }}
                            >
                              {typeof param.answer === "string" && param.answer.length > 30 ? (
                                <span className="cell-paragraph-ellipsis" title={param.answer}>
                                  {param.answer}
                                </span>
                              ) : (
                                param.answer ?? "--"
                              )}
                            </td>
                          ))
                        ) : (
                          <td
                            key={`${recordIndex}-empty`}
                            style={{
                              ...tableTdStyles,
                              backgroundColor:
                                selectedRowIndex === index ? "#f9faff" : "transparent",
                            }}
                          >
                            --
                          </td>
                        )
                      )}
                    {viewType !== "report" && (
                      <td style={{ ...tableTdStyles, textAlign: "center" }}>
                        {["Completed", "Approval_Pending", "In_Process"].includes(item.status) ? (
                          <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                            <button
                              type="button"
                              title="View Voice Analytics & CQA"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(item, index);
                              }}
                              style={{
                                background: "#e0effe",
                                border: "1px solid #bfdbfe",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                padding: "6px 10px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              type="button"
                              title="Sync / Inject Call to CQA"
                              onClick={(e) => handleSyncToCqa(item, e)}
                              disabled={syncingCallId === (item.id || item._id)}
                              style={{
                                background: "#ecfdf5",
                                border: "1px solid #a7f3d0",
                                color: "#059669",
                                cursor: syncingCallId === (item.id || item._id) ? "not-allowed" : "pointer",
                                padding: "6px 10px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s",
                                opacity: syncingCallId === (item.id || item._id) ? 0.7 : 1
                              }}
                            >
                              <RotateCw
                                size={16}
                                style={{
                                  animation: syncingCallId === (item.id || item._id) ? "spin 1s linear infinite" : "none"
                                }}
                              />
                            </button>
                          </div>
                        ) : (
                          <EyeOffIcon size={18} color="gray" style={{ cursor: "not-allowed", opacity: 0.5 }} />
                        )}
                      </td>
                    )}
                    {endSection?.map((header, i) => (
                      <td
                        key={i}
                        style={{
                          ...tableTdStyles,
                          backgroundColor:
                            selectedRowIndex === index ? "#f9faff" : "transparent",
                        }}
                        title={item[header.value] ?? "--"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(item, index);
                        }}
                      >
                        {header.value === "status" ? (
                          <StatusBadge status={item.status} />
                        ) : header.value === "disposition" ? (
                          <StatusBadge status={item.disposition} />
                        ) : typeof item[header.value] === "string" && item[header.value]?.length > 25 ? (
                          <span className="cell-paragraph-ellipsis" title={item[header.value]}>
                            {item[header.value]}
                          </span>
                        ) : (
                          item[header.value] ?? "--"
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      headers.length + (sections?.reduce((acc, s) => acc + (s.parameter?.length || 1), 0) || 0) + 1
                    }
                    style={tableTdStyles}
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>

        <Pagination
          pagination={pagination}
          postsPerPage={postsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          fetchDetail={fetchDetail}
        />
        <VoiceAnalyticsAiModel showModal={showModal} closeModal={closeModal} item={selectedRow} />
      </div>
    </>
  );
}

export default CallDetails;
